import { useContext, createContext, useEffect, useRef } from "react";
import { useAuth } from "./useAuth";
import { publish, subscribe, unsubscribe } from "../utils/events";
import { useChats } from "./useChats";
import { useTheme } from "./useTheme";
import {
  base64ToSingleArrayBuffer,
  convertBase64ToMP3,
  convertArrayBuffersToMP3,
  arrayBuffersToDataUrl,
  processCompletedAudioTransfer,
  sendToMobileApp,
} from "../utils/utils";
import { toast } from "react-toastify";
import api from "../services/api";
import { useTranslation } from "react-i18next";
import {
  isHttp404,
  confirmEncounterGone,
  resolveRecordingChatIdWithRetry,
  describeRecordingSaveError,
  RECORDING_SAVE_DEFERRED_OFFLINE,
} from "../utils/recordingSaveError";
import { isPoisonedRecording, poisonRecording } from "../utils/recordingPoisonRegistry";
import { isAuthError, isServerDownError, isStrikeableStatus, statusOf } from "../utils/serverErrors";
import {
  MAX_DIFF_REPLAY_STRIKE_DAYS,
  buildDiffReplayKey,
  clearDiffReplayFailure,
  recordDiffReplayFailure,
} from "../utils/offlineDiffPoisonRegistry";
import { isNonIdempotentRecording } from "../utils/recordingIdempotency";
import { isIdentityRefusal } from "../utils/recordingIdentityGuard";
import { findIdentityRefusal, forgetIdentityRefusal } from "../utils/nativeIdentityRefusals";
import {
  bindNativeRecordingId,
  markNativeRecoveryCandidate,
  readNativeRecordingMarker,
  resolveNativeRecordingUuid,
} from "../utils/nativeRecordingMarker";
import { buildNativeToastOptions } from "../utils/toastRailMetadata";
import {
  dismissRecordingProgressToast,
  getRecordingIdFromNativeToast,
  linkRecordingProgressToast,
  recordingProgressToastId,
  showRecordingProgressToast,
} from "../utils/recordingToast";

export const postMessagesContext = createContext();

const NATIVE_RECORDING_RETRY_WAKE_OFFSETS_MS = [45, 90, 180, 360].map((seconds) => seconds * 1000);

// Stand-in status for a replay failure that never reached the server (a diff
// whose body will not JSON.parse). Not a server verdict, and deliberately not
// eligible to form a "they all failed identically" incident.
const LOCAL_FAILURE_STATUS = 0;

function usePostMessagesProvider() {
  const { t } = useTranslation();
  const { isMobileApp, auth, setIsOnline, isOnline, isServerAvailable, checkLocation, brainId } = useAuth();
  const { parseRecording, chats, refreshList, updateEncounter } = useChats();
  const { activeTheme } = useTheme();
  const recordingRetryWakeRef = useRef();
  const lastProcessedMessage = useRef(new Set());
  const nativeToastAliasesRef = useRef(new Map());
  const lastStartWasRuralRef = useRef(false);
  const lastStartWasSilentRef = useRef(false);
  const recordingUserIdRef = useRef(auth?.user?.id);
  recordingUserIdRef.current = auth?.user?.id;
  // True while an offline replay batch is in flight (see the syncOfflineData
  // branch). A ref, not state: handleMessage reads and writes it synchronously.
  const offlineReplayRef = useRef(false);

  const audioChunkTransfers = useRef({});

  // Maps a native bridge toastId onto the recording capsule's stable id, so the
  // shell's upload toast and the FE's transcription toast are one mounted capsule.
  //
  // The event's OWN content is re-read first and the alias is only the fallback.
  // Checking the alias first meant that once a native id had been seen it kept
  // resolving to the first recording forever — a shell that recycles a transfer
  // id across recordings would have pointed the second one at the first one's
  // capsule. Only the message-less tail of the lifecycle (doneToast / dismissToast)
  // needs the fallback.
  const resolveNativeToast = (eventData = {}) => {
    if (!isMobileApp) return { ...eventData };

    const originalToastId = eventData.toastId;
    if (!originalToastId) return { ...eventData };

    const recordingId = getRecordingIdFromNativeToast(eventData) || nativeToastAliasesRef.current.get(originalToastId);
    if (!recordingId) return { ...eventData };

    nativeToastAliasesRef.current.set(originalToastId, recordingId);
    return { ...eventData, toastId: recordingProgressToastId(recordingId), isRecordingToast: true };
  };

  // dismissToast is the shell's last word on an upload, so the alias has no
  // further use. Dropping it bounds both the mis-alias window above and the
  // Map's growth — this provider lives for the whole webview session, which on
  // the native app can be days.
  const forgetNativeToastAlias = (toastId) => {
    if (toastId) nativeToastAliasesRef.current.delete(toastId);
  };

  // `progress: null` is the capsule's sentinel for "this work is no longer
  // measurable" and wipes the ring, so only WE may say it. The shell's values are
  // forwarded verbatim from the bridge, where a blank is just a missing number --
  // read as the sentinel it would blank a live upload mid-climb.
  const shellProgress = (value) => (Number.isFinite(value) ? value : undefined);

  // One string per shell stage, in the shell's own order: it transcodes to MP3
  // first, then uploads. recordingToast picks by stage, since that is what
  // tracks it -- calling the whole span "Uploading" would be wrong for the first
  // stretch of the bar.
  const recordingStageMessages = () => [
    t("hooks.usePostMessages.optimizingRecording"),
    t("hooks.usePostMessages.uploadingRecording"),
  ];

  // Id for the "this recording hasn't saved" toast, so a standing one can be
  // retracted when its claim stops being true.
  const stalledRecordingToastId = (recordingId) => `recording-stalled-${recordingId}`;

  /**
   * Releases a recording whose webview owner went silent, and tells the clinician.
   *
   * Built HERE, at provider scope, rather than inline in the syncRecording handler
   * that registers it. That handler's block scope holds the recording's audio --
   * `event.data.audio` is the whole base64 MP3 on the non-chunked path, and
   * `MP3Blob` is another copy as a data URL -- and V8 allocates one context per
   * scope, so a closure born in there keeps ALL of it reachable for as long as the
   * closure lives. Registered on the capsule (up to its 600s ceiling) and then held
   * by a sticky toast's onClick, that pinned tens of MB per recording for as long
   * as the toast stayed on screen. Taking only the id keeps the audio collectable
   * the moment the upload resolves, as it was before the ceiling handler existed.
   */
  const releaseStalledRecording = (recordingId) => {
    console.log("Recording " + recordingId + " stalled before any save was acked — returning it to the shell");
    sendRecordingUpdate(recordingId, "error");

    const toastId = stalledRecordingToastId(recordingId);
    // buildNativeToastOptions, NOT a bare options object: this only ever runs when
    // isMobileApp, and toastRailController.decorate() retimes any string-content
    // toast whose metadata lacks `preserveAutoClose` to the rail's 6s error
    // lifetime. A bare `autoClose: false` was silently overridden, so the message
    // meant to outlast a phone in a pocket vanished in six seconds.
    //
    // The rail also sets closeOnClick:false container-wide, so the tap dismisses
    // explicitly below instead of relying on react-toastify's default.
    toast.error(
      t("hooks.usePostMessages.recordingNotSavedRetry"),
      buildNativeToastOptions(
        { toastId, autoClose: false },
        {
          onClick: () => {
            // The lease was released above, so the poke is all that is left to make
            // the shell re-offer immediately instead of waiting out its backoff.
            sendRequestSync();
            toast.dismiss(toastId);
          },
        },
        { isMobileApp }
      )
    );
  };

  const scheduleRecordingRetryWakes = () => {
    if (recordingRetryWakeRef.current) clearTimeout(recordingRetryWakeRef.current);

    let wakeIndex = 0;
    const scheduleNextWake = () => {
      if (wakeIndex >= NATIVE_RECORDING_RETRY_WAKE_OFFSETS_MS.length) {
        recordingRetryWakeRef.current = null;
        return;
      }

      const previousOffset = wakeIndex === 0 ? 0 : NATIVE_RECORDING_RETRY_WAKE_OFFSETS_MS[wakeIndex - 1];
      const delay = NATIVE_RECORDING_RETRY_WAKE_OFFSETS_MS[wakeIndex] - previousOffset;
      recordingRetryWakeRef.current = setTimeout(() => {
        recordingRetryWakeRef.current = null;
        sendRequestSync();
        wakeIndex += 1;
        scheduleNextWake();
      }, delay);
    };

    scheduleNextWake();
  };

  // One failed replay of a queued offline diff, counted. Returns true when this
  // failure crossed the bound and the diff was therefore DROPPED (acked to the
  // shell) with its own distinct notice -- the caller then skips the ordinary
  // "still queued" retry toast, so exactly one message is shown.
  //
  // The trade: the shell never abandons a diff on its own, so retrying an
  // unreplayable one is unbounded by construction -- a deleted encounter or a
  // malformed payload re-offers and re-toasts on every sync, forever, with
  // nothing the user can do about it. Bounding the attempts and dropping the
  // diff with an honest "these changes were discarded" is the recording queue's
  // give-up philosophy (poisonRecording) applied here: stop pretending a dead
  // write is still in flight, and say so once.
  //
  // ONLY call this for a GENUINE rejection -- a real non-2xx response, or a
  // throw that is not outage-class. An unreachable server is not a verdict on
  // the diff, and counting outages here is how five reconnect flaps used to
  // destroy real clinical edits.
  const giveUpOnPoisonDiff = (chat, replayKey) => {
    // The temp- exemption used to be duplicated here; it now lives in
    // buildDiffReplayKey, which returns null for a create -- and
    // recordDiffReplayFailure(null) is 0, which can never reach the bound. One
    // rule, one place.
    const strikeDays = recordDiffReplayFailure(replayKey);
    if (strikeDays < MAX_DIFF_REPLAY_STRIKE_DAYS) return false;
    // Clear first: the ack below deletes the diff, so this counter is spent
    // either way, and a re-queued diff with the same key deserves a fresh round.
    clearDiffReplayFailure(replayKey);
    // toastId: a catch-up sync can carry several diffs for the same encounter,
    // and the shell re-offers on every wake. One notice per encounter, not a
    // burst the user has to dismiss one by one.
    toast.error(t("hooks.usePostMessages.offlineDiffGivenUp", { chatId: chat.chatId }), {
      toastId: `offlineDiffGivenUp:${chat.chatId}`,
    });
    sendSyncedEncounter(chat);
    console.warn(
      "Offline diff for encounter " +
        chat.chatId +
        " was rejected on " +
        strikeDays +
        " separate days; dropping it and telling the user"
    );
    return true;
  };

  const handleMessage = async (event) => {
    if (typeof event.data == "string") {
      try {
        event = JSON.parse(event.data);
      } catch (e) {
        event = {};
      }
    } else {
      event = event.data;
    }

    if (!event) return;

    if (event.deduplicationId) {
      if (lastProcessedMessage.current.has(event.deduplicationId)) {
        console.log("Duplicate message received, ignoring:", event.deduplicationId);
        return;
      }
      lastProcessedMessage.current.add(event.deduplicationId);
    }

    if (event.type === "authenticate") {
      // Auth with ios token
      let token = event.data.token;
      let user = event.data.user;
      let chats = event.data.chats;
      let org = event.data.org;
      publish("gotAuthToken", { token, user, chats, org });
    } else if (event.type === "otpCodeReceived") {
      // 2FA code captured natively (Android SMS User Consent or a push
      // notification carrying the code) and injected into the web login field.
      publish("gotOtpCode", { code: event.data?.code });
    } else if (event.type === "syncRecording-audioChunkStart") {
      const { transferId, metadata } = event.data;
      console.log("Starting chunked audio transfer:", transferId, "metadata:", JSON.stringify(metadata));
      audioChunkTransfers.current[transferId] = {
        chunks: {},
        receivedChunks: 0,
        metadata,
      };
      sendRecordingUpdate(metadata?.recordingId || event.data.recordingId, "parsing");
    } else if (event.type === "syncRecording-audioChunk") {
      const { transferId, chunkIndex, chunk } = event.data;
      const transfer = audioChunkTransfers.current[transferId];

      if (transfer) {
        transfer.chunks[chunkIndex] = base64ToSingleArrayBuffer(chunk);
        transfer.receivedChunks++;

        if (transfer.receivedChunks % 10 === 0) {
          console.log(`Audio transfer ${transferId}: ${transfer.receivedChunks} chunks`);
        }
      }
    } else if (event.type === "syncRecording-audioChunkEnd") {
      const { transferId, totalChunks } = event.data;

      const transfer = audioChunkTransfers.current[transferId];

      if (transfer) {
        transfer.totalChunks = totalChunks;
      }
    } else if (event.type === "syncRecording") {
      const recoveryUserId = auth?.user?.id;
      const pendingMarker = readNativeRecordingMarker(recoveryUserId, { ignoreGrace: true, ignoreAge: true });
      const recordingUuid = resolveNativeRecordingUuid(event.data, recoveryUserId);
      if (recordingUuid) {
        bindNativeRecordingId(recordingUuid, event.data.recordingId, recoveryUserId);
      }
      // Snapshot before any awaits: a late save must never nominate a newer
      // recording for confirmation, even when both share the same encounter.
      const recoveryCandidateUuid =
        !recordingUuid &&
        pendingMarker &&
        pendingMarker.chatId === event.data.chatId &&
        (!pendingMarker.nativeRecordingId || String(pendingMarker.nativeRecordingId) === String(event.data.recordingId))
          ? pendingMarker.recordingUuid
          : null;
      const syncToastId = isMobileApp
        ? recordingProgressToastId(event.data.recordingId)
        : `sync-recording-${event.data.recordingId}`;
      try {
        if (event.data.phase === "AI Update Note") {
          sendRecordingUpdate(event.data.recordingId, "parsed");
        } else {
          // Poison-pill short-circuit: this recording's encounter was already
          // CONFIRMED deleted (see the catch below). The native app re-offers
          // an "error" recording forever ("Never abandon" backoff) and its
          // recordingUpdate bridge has no non-retryable field, so answer each
          // re-offer here: consume the streamed audio buffers (free memory),
          // report "error" (audio kept on device, app backoff keeps growing),
          // and skip the upload + toasts — each retry used to be a fresh 404
          // and a support email, forever.
          const drainKey = recordingUuid || event.data.recordingId;
          if (isPoisonedRecording(drainKey, brainId)) {
            if (event.data.transferId) processCompletedAudioTransfer(audioChunkTransfers.current, event.data.transferId);
            console.log("Recording " + event.data.recordingId + " belongs to a deleted encounter — skipping sync (poisoned)");
            sendRecordingUpdate(event.data.recordingId, "error");
            return;
          }
          const syncMessage = isMobileApp
            ? t("hooks.usePostMessages.preparingTranscription")
            : t("hooks.usePostMessages.syncingRecording", { recordingId: event.data.recordingId });
          if (isMobileApp) {
            // The capsule uses the shell's row id; save completion uses the UUID
            // from this event or its verified legacy mapping. Link those names
            // so transcriptionCompleted can close the same progress capsule.
            linkRecordingProgressToast(drainKey, event.data.recordingId);
            // A previous attempt may have left the sticky "hasn't been saved yet"
            // toast on screen (its own ceiling, or the retry the clinician tapped).
            // This recording is moving again, so that claim is stale — retract it
            // rather than let a red failure sit above a live progress capsule.
            toast.dismiss(stalledRecordingToastId(event.data.recordingId));
            showRecordingProgressToast(toast, {
              toastId: syncToastId,
              message: syncMessage,
              // We are about to tell the shell "parsing", which makes it hand this
              // recording over and STOP re-offering it. Nothing guarantees we ever
              // report back: the webview can be reloaded, switched org, or hang
              // inside the MP3 transcode, and the shell would keep waiting forever
              // on an owner that no longer exists. So `parsing` is taken as a LEASE
              // — if the capsule goes silent all the way to its ceiling, give the
              // recording back as "error" (audio retained on the device AND
              // re-entered into the shell's retry backoff) instead of dismissing the
              // spinner and orphaning the audio, which is what used to happen.
              //
              // Retrying is only safe because Transcript - Save dedupes on the
              // recording id, so a re-offer can never double-create. The two voice
              // actions that are NOT deduped (Quick Start creates an encounter, AI
              // Update Note re-applies a section edit) get no handler at all — a
              // re-offer there would produce a second artifact.
              // Bound to the id only -- see releaseStalledRecording for why this
              // must not be an inline closure over this block's scope.
              onWorkingCeiling: isNonIdempotentRecording(event.data.chatId, event.data.phase)
                ? undefined
                : releaseStalledRecording.bind(null, event.data.recordingId),
              // The audio is already in the webview by now and the server reports
              // nothing until the transcript lands, so there is no measurable
              // progress left -- an inherited upload percentage would sit frozen.
              progress: null,
              options: buildNativeToastOptions({ toastId: syncToastId, autoClose: false }, {}, { isMobileApp }),
            });
          } else {
            toast.info(
              syncMessage,
              // Sticky by design and identified by design, so the rail must be
              // told: it neither retimes this toast nor lets the automatic
              // duplicate survivor outrank its explicit id. `closeButton: true`
              // stays the producer's own choice in a browser -- it is this
              // sticky toast's ONLY dismiss affordance there; on the rail, whose
              // contract is "no close button", it is suppressed.
              buildNativeToastOptions({ toastId: syncToastId, autoClose: false }, { closeButton: true }, { isMobileApp })
            );
          }
          console.log("Received record to sync " + event.data.recordingId);
          sendRecordingUpdate(event.data.recordingId, "parsing");
          let MP3Blob = null;

          if (!event.data.ruralMode && event.data.transferId) {
            const result = processCompletedAudioTransfer(audioChunkTransfers.current, event.data.transferId);

            console.log(
              "Completed audio transfer for recording",
              event.data.recordingId,
              result ? `with ${result.buffers.length} chunks, isMp3=${result.metadata?.isMp3}` : "without audio (result is null)"
            );

            if (result) {
              if (result.metadata?.isMp3) {
                console.log(`Audio is already MP3`);
                MP3Blob = arrayBuffersToDataUrl(result.buffers, "audio/mpeg");
              } else {
                console.log(`Converting to MP3`);
                MP3Blob = await convertArrayBuffersToMP3(result.buffers);
              }
            } else {
              console.warn(`No audio data found for transfer ${event.data.transferId}`);
            }
          } else if (!event.data.ruralMode && event.data.audio) {
            console.log(`Converting base64 audio`);
            MP3Blob = await convertBase64ToMP3(event.data.audio);
          }

          // A terminal bridge message is not proof that its audio arrived. A
          // missing chunk must remain retryable on the phone, never become an
          // empty save that also releases the frontend's pending-recording guard.
          const hasText = typeof event.data.textTranscription === "string" && event.data.textTranscription.trim().length > 0;
          if (!MP3Blob && !hasText) {
            throw new Error("Recording sync produced no audio");
          }

          const originalChatId = event.data.chatId;
          const chatId = await resolveRecordingChatIdWithRetry(api.getChatsByTempId, brainId, originalChatId);
          if (chatId !== originalChatId) {
            console.log("Resolved offline recording to durable encounter");
          }

          // Add delay to ensure offline created encounters are fully processed
          setTimeout(async () => {
            try {
              // Re-arm the capsule's silence clock at the upload's real start.
              //
              // The ceiling is documented as measuring SILENCE, not total lifetime,
              // and its safety claim ("reaching it means no save was acked") rests on
              // sitting above useTranscriptionWatchdog's 480s. But the two clocks have
              // different origins: this one started when the bridge message arrived,
              // while the watchdog cannot arm until api.transcript RESOLVES — after
              // chat-id resolution (up to ~31s), this 3s settle, the in-webview MP3
              // transcode and the entire upload. Left un-re-armed, all of that counted
              // as silence, so a slow-but-healthy save on weak hospital wifi was
              // declared dead, handed back to the shell as "error", and announced to
              // the clinician as unsaved — while it was still in flight.
              //
              // Guarded on the capsule still being MOUNTED: showRecordingProgressToast
              // mounts one when none is active, and the shell can report failure
              // (dismissToast) inside this 3s window. Re-showing then resurrected a
              // capsule for a recording that had already failed, and spun it for
              // another full ceiling.
              // Optional call, not a bare one: everything in this try reports "error"
              // to the shell on a throw, so a missing toast API here would turn a
              // perfectly good save into a hand-back. Unknown activity skips the
              // re-arm, which is the safe direction (the pre-existing ceiling still
              // applies; no capsule gets resurrected).
              if (isMobileApp && toast.isActive?.(syncToastId)) {
                showRecordingProgressToast(toast, { toastId: syncToastId, message: syncMessage, progress: null });
              }
              // A recording the guard refused at native handoff comes back through
              // here (the shell keeps it on "error" and re-offers it). The bridge
              // message carries no start time or patient, so re-judge it with the
              // identity stored at refusal; both sides then check it again.
              const priorRefusal = findIdentityRefusal(recoveryUserId, drainKey);
              const saveOutcome = await parseRecording(
                MP3Blob,
                chatId,
                event.data.phase,
                event.data.extraData && JSON.parse(event.data.extraData),
                event.data.textTranscription,
                // Backend dedup + manifest key on the UUID; fall back to the rowid
                // for older app builds that don't send recordingUuid yet.
                recordingUuid || event.data.recordingId,
                ...(priorRefusal
                  ? [
                      undefined,
                      undefined,
                      false,
                      {
                        deferred: true,
                        sourceChatId: originalChatId,
                        startedAt: priorRefusal.startedAt ?? undefined,
                        patientUserId: priorRefusal.patientUserId ?? undefined,
                        encounterUuid: priorRefusal.encounterUuid ?? undefined,
                      },
                    ]
                  : [])
              );
              if (isIdentityRefusal(saveOutcome)) {
                // Still refused: nothing was attached. "error" keeps the audio on the
                // phone, and the stored refusal stays so the next re-offer is judged too.
                dismissRecordingProgressToast(toast, syncToastId);
                sendRecordingUpdate(event.data.recordingId, "error");
                return;
              }
              if (saveOutcome === RECORDING_SAVE_DEFERRED_OFFLINE) {
                // The save was REFUSED (webview believes it is offline), so nothing
                // was uploaded, no transcription is running and the stall watchdog
                // was never armed — every signal that could retract the capsule
                // requires a save that did not happen. Left alone this spun
                // "Preparing your transcription" until its 600s ceiling, on a
                // recording the backend had never heard of.
                //
                // Hand the recording BACK as "error" rather than leaving it
                // "parsing": "parsing" claims the webview owns it, which retains the
                // audio but parks it — it took an app restart to be re-offered.
                // "error" keeps the audio on the device AND puts it back in the
                // shell's retry backoff, so it syncs itself once there is a
                // connection. The user already has the "will be saved when online"
                // info toast from parseRecording, so say nothing further here.
                dismissRecordingProgressToast(toast, syncToastId);
                sendRecordingUpdate(event.data.recordingId, "error");
                return;
              }
              if (priorRefusal) forgetIdentityRefusal(recoveryUserId, drainKey);
              // Automatic sync supplies a UUID; the installed manual Retry only
              // supplies its row id. A previously verified mapping preserves the
              // same backend dedup key and safely releases its pending guard.
              if (recordingUserIdRef.current === recoveryUserId) {
                publish("nativeRecordingRecovered", {
                  recordingUuid,
                  recordingId: event.data.recordingId,
                });
              }
              if (
                recoveryCandidateUuid &&
                recordingUserIdRef.current === recoveryUserId &&
                markNativeRecoveryCandidate(recoveryCandidateUuid, event.data.recordingId, recoveryUserId)
              ) {
                publish("nativeRecordingRecoveryNeedsConfirmation", {
                  recordingUuid: recoveryCandidateUuid,
                  recordingId: event.data.recordingId,
                });
              }
              // Don't update toast here - wait for SSE transcriptionCompleted event
              // The toast will be dismissed in useChats.js transcriptionCompleted handler
            } catch (e) {
              if (isMobileApp) dismissRecordingProgressToast(toast, syncToastId);
              else toast.dismiss(syncToastId);
              // Deleted encounter? Two-signal check: the save 404'd AND the
              // chat itself 404s. If confirmed, poison this recording (keyed
              // to the current brain — an org switch must not suppress a real
              // retry in the right org) so future native re-offers are
              // answered without another 404 round trip. The audio stays on
              // the device either way ("error" keeps it retained).
              if (e?.serverUnavailable) {
                // parseRecording already surfaced the honest "parsed when online"
                // info toast; a generic sync-error toast on top would contradict it.
                // The recording stays on the device and re-offers after recovery.
                console.log("Server unavailable while syncing recording — kept on device for retry");
              } else if (isHttp404(e) && (await confirmEncounterGone(api.getChat, chatId))) {
                poisonRecording(drainKey, brainId);
                toast.error(t("hooks.usePostMessages.recordingEncounterGone", { recordingId: event.data.recordingId }));
                console.log(
                  "Recording " +
                    event.data.recordingId +
                    " belongs to a deleted encounter — will not retry (audio kept on device)"
                );
              } else {
                toast.error(t("hooks.usePostMessages.errorSyncingRecording", { recordingId: event.data.recordingId }));
                console.error("Error syncing recording", describeRecordingSaveError(e, { recordingId: event.data.recordingId }));
              }
              sendRecordingUpdate(event.data.recordingId, "error");
            }
          }, 3000);
        }
      } catch (e) {
        if (isMobileApp) dismissRecordingProgressToast(toast, syncToastId);
        else toast.dismiss(syncToastId);
        toast.error(t("hooks.usePostMessages.errorSyncingRecording", { recordingId: event.data.recordingId }));
        console.error("Error syncing recording", describeRecordingSaveError(e, { recordingId: event.data.recordingId }));
        sendRecordingUpdate(event.data.recordingId, "error");
      }
    } else if (event.type === "recordingInProgress") {
      console.log("Recording in progress after restart");
      // The recorder needs the native start time to restore a capture after a
      // webview reload. Dropping it leaves the UI idle over a running recording.
      publish("gotRecordingInProgress", event.data);
    } else if (event.type === "showToast") {
      const nativeToast = resolveNativeToast(event.data);
      const { type, isRecordingToast } = nativeToast;
      const message = isRecordingToast ? t("hooks.usePostMessages.uploadingRecording") : nativeToast.message;
      // Same option chain the bridge has always produced, plus the rail metadata
      // React-Toastify's ToastItem cannot carry (explicit id, supplied
      // lifecycle, non-dismissibility, supplied action) and the per-toast
      // close-once guard. Nothing is invented here.
      const options = buildNativeToastOptions(nativeToast, {}, { isMobileApp });

      if (isRecordingToast) {
        if (!lastStartWasSilentRef.current) {
          showRecordingProgressToast(toast, {
            toastId: nativeToast.toastId,
            message,
            stageMessages: recordingStageMessages(),
            progress: shellProgress(nativeToast.progress),
            options,
          });
        }
      } else if (type === "error") {
        toast.error(message, options);
      } else if (type === "success") {
        toast.success(message, options);
      } else if (type === "warn") {
        toast.warn(message, options);
      } else {
        toast.info(message, options);
      }
    } else if (event.type === "updateToast") {
      const nativeToast = resolveNativeToast(event.data);
      const { toastId, progress, canDismiss, autoClose, isRecordingToast } = nativeToast;
      const message = isRecordingToast ? t("hooks.usePostMessages.uploadingRecording") : nativeToast.message;
      if (toastId) {
        if (isRecordingToast) {
          // `progress` is real: bytesProcessed / fileSize, emitted every fifth
          // chunk (lucasai-app hooks/usePostMessages.js:167). It used to be
          // handed to react-toastify's own bar, which the rail hides
          // (hideProgressBar), so it rendered nowhere. The capsule draws it.
          if (!lastStartWasSilentRef.current) {
            showRecordingProgressToast(toast, {
              toastId,
              message,
              stageMessages: recordingStageMessages(),
              progress: shellProgress(progress),
            });
          }
          return;
        }

        const updateOptions = {};
        if (progress !== undefined) {
          updateOptions.progress = progress;
        }
        if (message) {
          updateOptions.render = message;
        }
        if (canDismiss === false) {
          updateOptions.closeButton = false;
          updateOptions.closeOnClick = false;
        }
        if (autoClose !== undefined) {
          updateOptions.autoClose = autoClose;
        }
        toast.update(toastId, updateOptions);
      }
    } else if (event.type === "doneToast") {
      const nativeToast = resolveNativeToast(event.data);
      const { toastId, isRecordingToast } = nativeToast;
      if (toastId) {
        if (isRecordingToast && lastStartWasSilentRef.current) {
          // Silent action (Quick Start / AI Update Note). This is the event that
          // used to strand the capsule: it painted "Preparing your transcription"
          // for a lifecycle whose transcription never runs, so nothing could ever
          // retract it. Retract anything already on screen and say nothing.
          dismissRecordingProgressToast(toast, toastId);
          forgetNativeToastAlias(event.data?.toastId);
        } else if (isRecordingToast) {
          // Two different completions arrive on this one event.
          //
          // Offline (rural) mode: the shell transcodes and stops -- the audio is
          // on the device and nothing is in flight, so it fires doneToast right
          // after transcoding (Recorder.js:417). Saying "Preparing your
          // transcription" here promised server work that was never started, and
          // the capsule then sat until its ceiling. Tell the truth instead: it is
          // saved on the device and will sync itself once there is a connection,
          // and close on a checkmark because there is nothing left to wait for.
          //
          // Online: the upload has landed and the backend really is transcribing,
          // so the capsule stays open for the transcriptionCompleted hand-off.
          const savedOffline = lastStartWasRuralRef.current;
          showRecordingProgressToast(toast, {
            toastId,
            message: savedOffline
              ? t("hooks.usePostMessages.savedOfflineWillSync")
              : t("hooks.usePostMessages.preparingTranscription"),
            phase: savedOffline ? "ready" : "working",
            // Both completions end the measurable work: the upload is acknowledged
            // (online) or there was never an upload (offline). Server-side
            // transcription reports nothing until the transcript lands, so keeping
            // the upload's last percentage would freeze a dead number on screen for
            // the rest of the capsule's life.
            progress: null,
            autoDismissMs: savedOffline ? 4000 : undefined,
          });
        } else {
          toast.update(toastId, {
            render: nativeToast.message || t("hooks.usePostMessages.done"),
            type: "success",
            autoClose: 2400,
            progress: undefined,
            closeButton: false,
          });
        }
      }
    } else if (event.type === "dismissToast") {
      const { toastId, isRecordingToast } = resolveNativeToast(event.data);
      if (toastId) {
        // dismissToast is the shell's FAILURE signal, not a success hand-off.
        // Every one of its four senders in lucasai-app is a catch block:
        // Recorder.js:428 (immediate transcode failed), Recorder.js:610 (retry
        // transcode failed), Recordings.js:142 (file read/stream failed, which
        // also marks the recording "error" in the device DB), and
        // usePostMessages.js:206 (chunk upload failed). The shell's SUCCESS
        // signal is doneToast, which is where the "Preparing your transcription"
        // hand-off belongs and already lives.
        //
        // So this dismisses, for recording capsules too. Holding the capsule open
        // here told a doctor "Preparing your transcription" on a recording that
        // had just failed to upload -- the exact opposite of what happened, on
        // the one screen they use to decide whether the note is coming.
        toast.dismiss(toastId);
        if (isRecordingToast) dismissRecordingProgressToast(toast, toastId);
        forgetNativeToastAlias(event.data?.toastId);
      }
    } else if (event.type === "microphoneNotAvailable") {
      toast.error(t("hooks.usePostMessages.microphoneNotAvailable"));
      publish("gotMicrophoneNotAvailable");
    } else if (event.type === "micCheckVolume") {
      publish("gotMicCheckVolume", event.data);
    } else if (event.type === "micCheckResult") {
      publish("gotMicCheckResult", event.data);
    } else if (event.type === "micCheckStopped") {
      publish("gotMicCheckStopped", event.data);
    } else if (event.type === "isOnForeground") {
      try {
        let response = await api.getUserProfile();
        if (response.status === 200 && response.data) {
          checkLocation(response.data.user);
        }
      } catch (e) {
        // Ignore error because mobile app may be offline
      }
    } else if (event.type === "volumeLevel") {
      publish("gotVolumeLevel", { level: event.data.level });
    }
    // Offline mode
    else if (event.type === "onConnectionStatusChange") {
      // NetInfo's isInternetReachable is TRI-state: true, false, or null for "not
      // determined yet". This used to be assigned to isOnline verbatim, so null —
      // "unknown" — became "offline", and isOnline gates every offline-aware write
      // in useChats, the transcript save included. On networks that block NetInfo's
      // reachability probe (hospital wifi, captive portals, VPNs) null is not even
      // transient: it can hold for the whole session while our own API calls keep
      // returning 200, and since NetInfo only emits on CHANGE nothing arrives to
      // correct it. Unknown is not a state change — hold what we have and wait for a
      // real true/false rather than inventing either answer.
      const reachable = event.data?.isInternetReachable;
      if (reachable == null) return;
      setIsOnline((isOnline) => {
        if (reachable !== isOnline) {
          if (reachable) {
            toast.success(t("hooks.usePostMessages.backOnline"));
            if (!auth || !auth.user || !auth.user.id) {
              toast.warn(t("hooks.usePostMessages.reAuthRequired"));
              sendRestartIframe();
              // login(event.data.user, undefined, event.data.org, undefined, event.data.loginToken);
            } else {
              // sendRequestSync();
            }
          } else {
            console.log("Device is offline");
            toast.warn(t("hooks.usePostMessages.nowOffline"));
          }
        }

        return reachable;
      });
    } else if (event.type === "syncOfflineData") {
      // alert("Syncing offline data"); // --- IGNORE ---

      // DEFER the whole replay while the backend is unreachable.
      //
      // This is not a rare edge: the shell calls syncDataAndRecordings()
      // SYNCHRONOUSLY on reconnect, before the async healthcheck has posted
      // onConnectionStatusChange -- and again on every app launch and every
      // requestSync wake. So a replay routinely arrives while isServerAvailable
      // is still false, where every write is doomed by construction.
      //
      // Replaying anyway does not merely waste requests: it MANUFACTURES the
      // failures the poison counter strikes on, so a handful of reconnect flaps
      // could reach the bound and acknowledge-then-delete edits the server never
      // saw. Returning without acking anything is free -- the shell never
      // abandons a diff and re-offers the entire queue on the next sync.
      //
      // The session must be live too, not merely the server. `auth.offline` is
      // the offline STUB (useAuth's loginOffline), which carries a user.id
      // copied out of localStorage and would sail past a naive id check while
      // having no session behind it at all.
      if (isMobileApp && (!isServerAvailable || !auth?.user?.id || auth?.offline)) {
        console.log("Offline sync deferred: no live session yet; the shell will re-offer the queue");
        return;
      }

      // Re-entrancy latch. useAuth's latched announcement covers the wakes WE
      // publish, but it cannot control the shell: syncDataAndRecordings() fires
      // on its own reconnect, requestSync wakes fire on 45/90/180/360s offsets
      // after a successful sync, and lastProcessedMessage dedupes only by
      // deduplicationId — which differs between two genuine deliveries of the
      // same queue. Two loops over one queue create two durable encounters. A
      // ref, not state: handleMessage's closure is rebuilt on every render, so
      // this has to be readable and writable synchronously.
      if (offlineReplayRef.current) {
        console.log("Offline replay already running; ignoring this batch (the shell re-offers)");
        return;
      }
      offlineReplayRef.current = true;
      try {
        await runOfflineReplay(event.data);
      } finally {
        offlineReplayRef.current = false;
      }
    } else {
      // console.log("Unknown event from mobile app", event);
    }
  };

  // Batch-incident guard. "Every diff in the queue failed identically" is
  // overwhelmingly a broken server, not three independently malformed payloads
  // -- and 500 is strikeable precisely because this backend's permanent
  // rejections arrive as bare 500s, which is also exactly what a fleet-wide
  // incident answers to every write. So look at the batch before striking any
  // of it.
  //
  // Threshold 3 DISTINCT encounters keeps it from misfiring on a one- or
  // two-diff queue, where the one-strike-per-day rule is the only protection
  // needed and is already cheap.
  //
  // Only REAL server statuses may form an incident. A failure that never reached
  // the server -- a diff whose body will not JSON.parse -- is recorded as
  // LOCAL_FAILURE_STATUS, and three of those would otherwise share one "status"
  // and be waved through as a fleet-wide outage forever. They are the exact
  // opposite: permanently unreplayable payloads, the population the bound exists
  // for. So they are excluded from the comparison AND still struck even when the
  // server-verdict half of the batch is judged an incident.
  const applyIntendedStrikes = (intended) => {
    if (intended.length === 0) return;
    const serverVerdicts = intended.filter((item) => item.status !== LOCAL_FAILURE_STATUS);
    const distinctChats = new Set(serverVerdicts.map((item) => item.chat.chatId)).size;
    const oneStatus = new Set(serverVerdicts.map((item) => item.status)).size === 1;
    const looksLikeIncident = serverVerdicts.length > 0 && distinctChats >= 3 && oneStatus;
    if (looksLikeIncident) {
      console.warn(
        "Offline replay: " +
          serverVerdicts.length +
          " diffs all failed " +
          serverVerdicts[0].status +
          " — treating as a server incident, no strikes"
      );
    }
    const toStrike = looksLikeIncident ? intended.filter((item) => item.status === LOCAL_FAILURE_STATUS) : intended;
    toStrike.forEach(({ chat, replayKey }) => {
      if (giveUpOnPoisonDiff(chat, replayKey)) return;
      toast.error(t("hooks.usePostMessages.errorSyncingOfflineData", { chatId: chat.chatId }), {
        toastId: `offlineDiffError:${chat.chatId}`,
      });
    });
  };

  const runOfflineReplay = async (queue) => {
    let sync = 0;
    // Strikes are COLLECTED, not applied inline, so the batch-incident guard
    // below can look at the shape of the whole batch first.
    const intended = [];
    for (let chat of queue) {
      // Bounded-retry identity for this queued diff, computed BEFORE the parse
      // so a malformed body (which throws below) is still countable. Queue
      // identity only -- never the body, which is PHI.
      const replayKey = buildDiffReplayKey(chat);
      try {
        let encounter = JSON.parse(chat.diff);
        if (chat.chatId.startsWith("temp-")) {
          // Idempotence belt, and the only one that survives a delivery we do
          // not control. The shell can hand the same queue over twice (its own
          // reconnect delivery, a requestSync wake, a retry wake) and a
          // duplicate create files a second REAL encounter plus, via the branch
          // below, a second patient. `offlineId` is the durable stamp the create
          // writes and the recording resolver reads, so ask for it first --
          // api.getChatsByTempId is the exact lookup, and it already exists.
          // Cost: one GET per temp- diff per replay.
          const existing = await api.getChatsByTempId(brainId, chat.chatId);
          if (existing?.data?.chats?.length) {
            console.log("Offline create already landed for " + chat.chatId + "; acking without re-creating");
            clearDiffReplayFailure(replayKey);
            sync++;
            sendSyncedEncounter(chat);
            continue;
          }

          if (!encounter.userId) {
            encounter.user.groups = ["patient"];

            let patientSearch = await api.findPatientByEmail(encounter.user.email);
            if (!patientSearch.data.users[0]) {
              let patientResponse = await api.createPatient(encounter.user);
              encounter.userId = patientResponse.data.userId;
              console.log("OFFLINE - Created patient " + patientResponse.data.userId);
            } else {
              encounter.userId = patientSearch.data.users[0].id;
              console.log("OFFLINE - Found existing patient " + patientSearch.data.users[0].id);
            }
          }

          // Every temp- encounter needs this stamp, not just the new-patient
          // branch: the recording resolver finds the durable encounter by
          // `offlineId`, so one created without it is unreachable and its
          // native recordings re-error forever.
          encounter.contextVariables = encounter.contextVariables || {};
          encounter.contextVariables["local:offlineId"] = chat.chatId;
          encounter.contextVariables["local:diffId"] = chat.id;
          delete encounter.chatId;
          delete encounter.user;
          if (encounter.brainId === "null") delete encounter.brainId;

          // The only caller allowed to create without a resolvable location.
          // This is a replay of a recording queued while offline, with nobody
          // present to ask — refusing it would strand a captured clinical
          // encounter, which is worse than filing one whose location has to be
          // corrected later. api.createChat stamps `local:locationUnresolved`
          // on anything it lets through this way.
          await api.createChat(encounter, { allowUnresolvedLocation: true });
          console.log("Offline data synced for new encounter");
          clearDiffReplayFailure(replayKey);
          sync++;
          sendSyncedEncounter(chat);
        } else {
          // A queued diff is a whole-chat snapshot, so it CARRIES assignedTo
          // -- and normalizeAssignedToForNative already converted it to the
          // exact string shape the backend's "Note - Update" applies. skipAssign
          // below stops the GATE from re-deciding ownership here; it does
          // nothing about the payload, which transfers the chart on its own
          // just by being replayed. Edit a colleague's vitals offline at 09:00,
          // reconnect at 17:00, and this would hand their chart to whoever was
          // offline eight hours ago, over the top of whoever owns it now, with
          // no user present to see it happen.
          //
          // So the replay never speaks about ownership at all: dropping the key
          // leaves the server's current assignedTo untouched (Note - Update only
          // writes the params it is given) while every other edited field still
          // lands, which is what makes an offline edit on the user's OWN
          // encounter replay exactly as before -- the server already has them as
          // the owner, and nothing here contradicts it. Legitimate transfers are
          // the online gate's job (ensureAssignedToMe, useChats.js).
          //
          // Stripped HERE rather than where the payload is built so it also
          // covers diffs the native app queued before this shipped -- those are
          // already on disk with assignedTo in them, and this is the only code
          // that reads them back. The temp- create branch above is deliberately
          // untouched: a brand-new encounter has no prior owner to steal from,
          // and its assignedTo is the creating clinician's own.
          delete encounter.assignedTo;

          // skipAssign: the native app's offline sync loop, replaying queued
          // encounters on reconnect with no user present. Same reverse-steal
          // hazard as the ICD-10 replay in useChats.
          //
          // surfaceVerdict: ask updateEncounter what actually HAPPENED instead
          // of inferring it from a status that a rejection never carries (axios
          // rejects every non-2xx, so the old `res?.status !== 200` test could
          // not see a server verdict at all). Ack ONLY "ok" -- acking anything
          // else tells the shell to DELETE a diff that never landed.
          const res = await updateEncounter(encounter, false, undefined, {
            skipAssign: true,
            surfaceVerdict: true,
          });
          if (res?.verdict === "ok") {
            console.log("Offline data synced for encounter " + chat.chatId);
            clearDiffReplayFailure(replayKey);
            sync++;
            sendSyncedEncounter(chat);
            continue;
          }
          if (res?.verdict === "unavailable" || res?.verdict === "unauthorized") {
            // Neither is a verdict on the diff: the server was not there, or it
            // declined to LOOK. No strike, no toast -- and BREAK, because the
            // rest of the queue faces the same wall and would burn a 300s axios
            // timeout each. The shell re-offers the whole queue.
            console.warn("Offline replay stopped at encounter " + chat.chatId + " (" + res.verdict + "); queued, no strike");
            break;
          }
          if (res?.verdict === "rejected" && isStrikeableStatus(res.status)) {
            intended.push({ chat, replayKey, status: res.status });
            continue;
          }
          // refetchFailed / queuedOffline / invalid / an unrecognised shape: all
          // "the server said nothing about this payload".
          console.warn(
            "Offline diff for encounter " +
              chat.chatId +
              " did not sync (" +
              (res?.verdict || "unknown") +
              (res?.status ? " " + res.status : "") +
              "); leaving it queued, no strike"
          );
        }
      } catch (e) {
        console.warn("Error syncing offline data");
        if (e?.tempIdLookupTimedOut) {
          // The dedupe lookup for THIS create hung past its client bound
          // (api.getChatsByTempId). That says nothing about the server — the
          // interceptor deliberately does not announce an abort as an outage —
          // and nothing about the diff, so: no strike, leave it queued for the
          // shell to re-offer, and CONTINUE rather than break. The shell queues
          // one whole-chat snapshot per chat, so nothing behind this create
          // depends on it; breaking here parked every later diff until the next
          // app launch, with no recovery wake left to re-drive the replay.
          console.warn("Offline create " + chat.chatId + " could not be deduped in time; queued, no strike");
          continue;
        }
        if (isServerDownError(e) || isAuthError(e)) {
          // The server went away mid-replay (or was never really back), or
          // the session had lapsed and it declined to look at the payload.
          // Either way the diff is untouched and still queued, and neither
          // says anything about the diff itself -- so this is the same
          // non-event as the deferral above: no strike, and no error toast
          // claiming a failure that has not happened. It replays on the next
          // sync, once useAuth has the session back.
          //
          // BREAK, don't continue: twenty more diffs against a dead server or a
          // lapsed session is twenty 300s axios timeouts (api.js) of doomed
          // requests, and the shell re-offers the whole queue anyway.
          console.warn("Offline diff for encounter " + chat.chatId + " hit an outage or lapsed session; queued, no strike");
          console.error("Error syncing offline data", describeRecordingSaveError(e));
          break;
        }
        // Everything else that escapes here is a genuine permanent defect in
        // THIS diff -- most concretely a JSON.parse of a malformed body, which
        // no amount of retrying will fix. Collected like a rejection so the
        // incident guard sees the whole batch before anything is struck.
        // A create's key is null, so a temp- row can never accrue one.
        intended.push({ chat, replayKey, status: statusOf(e) ?? LOCAL_FAILURE_STATUS });
        console.error("Error syncing offline data", describeRecordingSaveError(e));
      }
    }

    applyIntendedStrikes(intended);

    refreshList();

    if (sync > 0) {
      toast.success(t("hooks.usePostMessages.encountersSynced", { count: sync }));
      scheduleRecordingRetryWakes();
    }
  };

  useEffect(() => {
    sendIsReady();
  }, []);

  useEffect(() => {
    return () => {
      if (recordingRetryWakeRef.current) {
        clearTimeout(recordingRetryWakeRef.current);
        recordingRetryWakeRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // NOTE: listen on both window (iOS) and document (Android) for
    // cross-platform compatibility. Deduplication is handled via
    // lastProcessedMessage in handleMessage
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
    // isServerAvailable belongs in here: handleMessage's offline-replay guard
    // reads it, and without the dep the listener keeps a closure from before
    // the outage was detected -- i.e. the guard would answer with a stale
    // "server is fine" during exactly the reconnect window it exists for.
    // eslint-disable-next-line
  }, [auth, chats, isOnline, isServerAvailable, isMobileApp]);

  useEffect(() => {
    if (isMobileApp && auth && auth.user && auth?.user?.id) {
      let hostName = localStorage.getItem("hostName");
      let organization = localStorage.getItem("organization");
      let language = auth?.user?.language || "en";
      let locationId = auth?.user?.custom?.actualLocationId || auth?.user?.currentLocationId || "";

      sendAuthTokenToApp(
        auth.user.loginToken,
        auth.user.email,
        auth?.user?.id,
        organization,
        hostName,
        brainId,
        language,
        locationId
      );
    }
    // eslint-disable-next-line
  }, [
    isMobileApp,
    auth?.user?.id,
    auth?.user?.language,
    auth?.user?.currentLocationId,
    auth?.user?.custom?.actualLocationId,
    brainId,
  ]);

  useEffect(() => {
    if (isMobileApp && activeTheme) {
      sendThemeToApp(activeTheme);
    }
    // eslint-disable-next-line
  }, [isMobileApp, activeTheme]);

  // Recovery wake (spec §4 step 2): re-ask the shell for its offline diffs once
  // the app is genuinely back.
  //
  // Something HAS to re-ask, because syncOfflineData DEFERS the whole batch
  // while the server is unreachable, and the shell's own reconnect delivery —
  // fired synchronously, before the connection status is established — is
  // routinely the one that gets deferred.
  //
  // ONE subscription, for BOTH flavors. There used to be a second mechanism
  // here — a `cameOnline && serverReachable` edge effect for the connectivity
  // flavor — on the claim that it and serverBackOnline were mutually exclusive
  // by construction. They were not (see useAuth's announceRecovered), and each
  // overlap asked the shell twice for one queue. useAuth now owns the whole
  // decision, latched, and this hook just does what it is told.
  useEffect(() => {
    const onServerBack = () => {
      if (isMobileApp) sendRequestSync();
    };
    subscribe("serverBackOnline", onServerBack);
    return () => unsubscribe("serverBackOnline", onServerBack);
    // eslint-disable-next-line
  }, [isMobileApp]);

  // Send Events
  const sendAuthTokenToApp = (token, user, userId, org, hostName, brainId, language, locationId) => {
    sendToMobileApp({
      type: "saveToken",
      token,
      user,
      userId,
      org,
      hostName,
      brainId,
      language,
      locationId,
    });
  };

  // const sendIsVisible = () => {
  //   sendToMobileApp({ type: "isVisible" });
  // }

  // const sendIsActive = () => {
  //   sendToMobileApp({ type: "isActive" });
  // }

  const sendIsReady = () => {
    sendToMobileApp({ type: "isReady" });
  };

  const sendOnLoadLogin = () => {
    sendToMobileApp({ type: "onLoadLogin" });
  };

  // Tell native to start/stop listening for an incoming 2FA code (Android SMS
  // User Consent). Native replies with an "otpCodeReceived" message.
  const sendStartOtpListener = () => {
    sendToMobileApp({ type: "startOtpListener" });
  };

  const sendStopOtpListener = () => {
    sendToMobileApp({ type: "stopOtpListener" });
  };

  const sendSwitchVersion = () => {
    sendToMobileApp({ type: "switchVersion", version: "old" });
  };

  // Recordings
  const sendStartRecording = (chatId, user, phase, extraData, ruralMode, recordingUuid) => {
    // Remembered so doneToast can tell the two completions apart. The shell's
    // record id is its OWN (it stores our recordingUuid as a separate `uuid`
    // field, Recorder.js:304), so the capsule's id cannot be correlated back to
    // this call -- but recordings are serialized, so the last armed mode is the
    // mode of the recording that just finished. Rural is decided here in the
    // frontend (useRecorder.js:725), which is why this needs no app release.
    lastStartWasRuralRef.current = Boolean(ruralMode);
    // No progress capsule for the two non-idempotent voice actions. Both save
    // through a NON-transcript endpoint (see recordingIdempotency.js), so the
    // transcription that would complete a capsule never runs and it span until
    // its ceiling:
    //   - Quick Start records before an encounter exists, then opens ModalNewChat
    //     for the patient details -- a human-length pause mid-lifecycle.
    //   - AI Update Note is section dictation; the section rewrites itself, so a
    //     separate progress toast is noise on top of a visible result.
    // Errors are unaffected: they are their own toasts, not this capsule.
    lastStartWasSilentRef.current = isNonIdempotentRecording(chatId, phase);
    sendToMobileApp({ type: "startRecording", chatId, user, phase, extraData, ruralMode, recordingUuid });
  };

  const sendStopRecording = () => {
    sendToMobileApp({ type: "stopRecording" });
  };

  const sendRecordingUpdate = (recordingId, state) => {
    sendToMobileApp({ type: "recordingUpdate", recordingId, state });
  };

  const sendShowRecordings = () => {
    sendToMobileApp({ type: "showRecordings" });
  };

  // Offline data
  const onChatListUpdate = (list) => {
    sendToMobileApp({ type: "onChatListUpdate", list });
  };

  const onChatUpdate = (chat) => {
    sendToMobileApp({ type: "onChatUpdate", chat });
  };

  const sendSyncedEncounter = (diff) => {
    sendToMobileApp({ type: "syncedEncounter", diff });
    // An offline CREATE that just landed. useChats pins unsynced temp- rows on
    // top of every fetched window so offline work never disappears from the
    // list; this ack is what lets it retire the pinned row once the real
    // encounter exists, and is the backstop for the durable-twin join in case
    // web/organization-chats does not project the `local:offlineId` stamp back.
    // Only creates carry a temp- id -- an update diff names an encounter the
    // list already shows under its real id, and has nothing to retire.
    if (typeof diff?.chatId === "string" && diff.chatId.startsWith("temp-")) {
      publish("offlineEncounterSynced", { tempId: diff.chatId });
    }
  };

  const sendRestartIframe = () => {
    sendToMobileApp({ type: "restartIframe" });
  };

  const sendRequestSync = () => {
    sendToMobileApp({ type: "requestSync" });
  };

  // Dev Mode
  const sendSetConnectionStatus = (status) => {
    sendToMobileApp({ type: "setConnectionStatus", status });
  };

  const sendSetIframeURL = (url) => {
    sendToMobileApp({ type: "setIframeURL", url });
  };

  const sendSetIframeLocal = () => {
    sendToMobileApp({ type: "setIframeLocal" });
  };

  // Offline data
  const sendPreventScreenshot = (list) => {
    sendToMobileApp({ type: "preventScreenshot", list });
  };

  const sendAllowScreenshot = (list) => {
    sendToMobileApp({ type: "allowScreenshot", list });
  };

  const sendOpenPdf = (base64, filename) => {
    return sendToMobileApp({ type: "openPdf", data: base64, filename });
  };

  const sendThemeToApp = (theme) => {
    sendToMobileApp({ type: "themeChange", theme });
  };

  const sendAppUpdateInfo = (updateInfo) => {
    sendToMobileApp({ type: "appUpdateInfo", ...updateInfo });
  };

  // Return
  return {
    sendAppUpdateInfo,
    sendOnLoadLogin,
    sendStartOtpListener,
    sendStopOtpListener,
    sendSwitchVersion,
    sendStartRecording,
    sendStopRecording,
    sendRecordingUpdate,
    sendShowRecordings,
    sendRequestSync,
    onChatListUpdate,
    onChatUpdate,
    sendSetConnectionStatus,
    sendSetIframeURL,
    sendSetIframeLocal,
    sendPreventScreenshot,
    sendAllowScreenshot,
    sendOpenPdf,
    sendIsReady,
  };
}

export function ProvidePostMessages({ children }) {
  const values = usePostMessagesProvider();
  return <postMessagesContext.Provider value={values}>{children}</postMessagesContext.Provider>;
}

export const usePostMessages = () => {
  return useContext(postMessagesContext);
};
